/**
 * Cross-platform cron primitives shared by the web and native schedule editors.
 * Pure logic, no framework imports — so both platforms format times, describe a
 * raw cron string, validate cron syntax, and parse an expression into structured
 * schedule state identically.
 *
 * `parseCron` decomposes a 5-field expression into a {@link CronSchedule} (repeat
 * kind + "every N" interval + time + day selection). Native drives its editor
 * off this shape directly; web maps it onto its own frequency buckets. Building a
 * cron string back from structured state stays platform-local, since the two
 * editors model the reverse direction differently.
 */

import { ordinal } from "./ordinal.ts";

export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** The same days spelled out, for descriptions and full-width day pickers. */
export const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;
const MONTH_NAMES = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CRON_FIELD_RE = /^[\d*,/-]+$/;

/** Returns true if `s` is a syntactically valid 5-field cron expression. */
export const isValidCron = (s: string): boolean => {
  const parts = s.trim().split(/\s+/);
  return parts.length === 5 && parts.every((p) => CRON_FIELD_RE.test(p));
};

export type Period = "AM" | "PM";

/** Split a 24-hour value (0–23) into its 12-hour display hour (1–12) and period. */
export const to12Hour = (hour24: number): { hour12: number; period: Period } => ({
  hour12: hour24 % 12 === 0 ? 12 : hour24 % 12,
  period: hour24 >= 12 ? "PM" : "AM",
});

/** Recombine a 12-hour display hour (1–12) and period back into 24-hour (0–23). */
export const to24Hour = (hour12: number, period: Period): number => {
  const base = hour12 % 12; // 12 → 0
  return period === "PM" ? base + 12 : base;
};

/** Format a 24-hour time as a 12-hour clock string, e.g. `(14, 30)` → "2:30 PM". */
export const formatTime12 = (hour: number, minute: number): string => {
  const { hour12, period } = to12Hour(hour);
  return `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
};

/**
 * Concise 12-hour time for short schedule labels: on-the-hour drops the minutes,
 * so `(9, 0)` → "9 AM" and `(14, 30)` → "2:30 PM".
 */
export const formatTime12Short = (hour: number, minute: number): string => {
  const { hour12, period } = to12Hour(hour);
  return minute === 0
    ? `${hour12} ${period}`
    : `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
};

const parseNumber = (field: string, min: number, max: number): number | null => {
  if (!/^\d+$/.test(field)) return null;
  const n = Number(field);
  return n >= min && n <= max ? n : null;
};

/** "9 AM", "9 AM and 5 PM", "9 AM, 1 PM and 5 PM" — prose, not a bare comma list. */
const joinProse = (items: string[]): string =>
  items.length <= 1
    ? (items[0] ?? "")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/**
 * Every time of day a schedule fires at, in clock order. A 5-field cron has one
 * minute field shared by every hour it lists, so several times always share a
 * minute: `30 9,17 * * *` is 9:30 AM and 5:30 PM, and nothing can say 9:00 and
 * 5:30 in one expression.
 */
export const formatTimes12 = (hours: readonly number[], minute: number): string =>
  joinProse([...hours].sort((a, b) => a - b).map((h) => formatTime12(h, minute)));

/** {@link formatTimes12} in the concise form of {@link formatTime12Short}. */
export const formatTimes12Short = (hours: readonly number[], minute: number): string =>
  joinProse([...hours].sort((a, b) => a - b).map((h) => formatTime12Short(h, minute)));

/**
 * Read a cron hour field that names specific hours — one, or a comma list —
 * into sorted, de-duplicated hours, or null when it is a step, a wildcard, a
 * range, or out of range.
 */
export const parseHourList = (field: string): number[] | null => {
  const parts = field.split(",");
  const hours = parts.map((p) => parseNumber(p, 0, 23));
  if (hours.some((h) => h === null)) return null;
  return [...new Set(hours as number[])].sort((a, b) => a - b);
};

/** The hour field for {@link parseHourList}'s output: `9` or `9,13,17`. */
export const buildHourList = (hours: readonly number[]): string =>
  [...new Set(hours)].sort((a, b) => a - b).join(",");

const describeField = (field: string, names?: string[]): string => {
  if (field === "*") return "";
  if (field.startsWith("*/")) return `every ${field.slice(2)}`;
  const parts = field.split(",");
  if (names) {
    return parts
      .map((p) => {
        if (p.includes("-")) {
          const [a, b] = p.split("-");
          return `${names[Number(a)] ?? a} through ${names[Number(b)] ?? b}`;
        }
        return names[Number(p)] ?? p;
      })
      .join(", ");
  }
  return parts.join(", ");
};

/** Human-readable description of a 5-field cron string, or null if malformed. */
export const describeCron = (cron: string): string | null => {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minStr, hourStr, domStr, monStr, dowStr] = parts;
  const pieces: string[] = [];

  if (minStr.startsWith("*/")) {
    pieces.push(`Every ${minStr.slice(2)} minutes`);
  } else if (hourStr.startsWith("*/")) {
    pieces.push(`Every ${hourStr.slice(2)} hours`);
  } else if (hourStr === "*") {
    pieces.push(`Every hour at minute ${minStr}`);
  } else {
    const min = parseNumber(minStr, 0, 59);
    const hours = parseHourList(hourStr);
    if (min !== null && hours !== null) {
      pieces.push(`At ${formatTimes12(hours, min)}`);
    } else {
      pieces.push(`At minute ${minStr}, hour ${hourStr}`);
    }
  }

  if (domStr !== "*") {
    pieces.push(`on day ${describeField(domStr)} of the month`);
  }
  if (monStr !== "*") {
    pieces.push(`in ${describeField(monStr, MONTH_NAMES)}`);
  }
  if (dowStr !== "*") {
    pieces.push(`on ${describeField(dowStr, [...DAY_NAMES])}`);
  }
  return pieces.join(" ");
};

/** How often a schedule repeats. `weekdays` is the Mon–Fri preset of `weekly`. */
export type CronRepeat = "minutely" | "hourly" | "daily" | "weekdays" | "weekly" | "monthly";

/**
 * A cron expression decomposed for editing. `interval` is the "every N" step for
 * the repeat's own unit (minutes / hours / days / months); `weekdays` and
 * `weekly` are always 1 because cron can't express "every N weeks".
 * `hours`/`minute` are meaningless for `minutely`, and `hours` for `hourly` —
 * they carry the last-known time so switching repeats doesn't lose it.
 *
 * `hours` is every hour the schedule fires at, sorted, never empty. They share
 * the one `minute`, because a 5-field cron has a single minute field: "9:00 and
 * 5:00" is one expression, "9:00 and 5:30" is not.
 */
export type CronSchedule = {
  repeat: CronRepeat;
  interval: number;
  hours: number[];
  minute: number;
  daysOfWeek: number[];
  dayOfMonth: number;
};

/** Read a `*` / `*\/N` step field, returning the step (1 for `*`) or null. */
const parseStep = (field: string, max: number): number | null => {
  if (field === "*") return 1;
  const match = field.match(/^\*\/(\d+)$/);
  if (!match) return null;
  const n = Number(match[1]);
  return n >= 1 && n <= max ? n : null;
};

/** Fallback time for repeats that carry no time of day (minutely / hourly). */
const PLACEHOLDER_TIME = { hours: [9], minute: 0 };

/**
 * Parse a 5-field cron string into structured schedule state, or null if it
 * doesn't match a supported pattern.
 */
export const parseCron = (cron: string): CronSchedule | null => {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [minField, hourField, domField, monField, dowField] = parts;

  const unconstrainedDate = domField === "*" && monField === "*" && dowField === "*";

  // A minute step repeats within the hour, so it only reads as "minutely" when
  // no hour narrows it ("*/15 9 * * *" is a shape the editor can't round-trip).
  if (minField === "*" || minField.startsWith("*/")) {
    const interval = parseStep(minField, 59);
    if (interval === null || hourField !== "*" || !unconstrainedDate) return null;
    return { repeat: "minutely", interval, ...PLACEHOLDER_TIME, daysOfWeek: [], dayOfMonth: 1 };
  }

  const minute = parseNumber(minField, 0, 59);
  if (minute === null) return null;

  if (hourField === "*" || hourField.startsWith("*/")) {
    const interval = parseStep(hourField, 23);
    if (interval === null || !unconstrainedDate) return null;
    return {
      repeat: "hourly",
      interval,
      hours: PLACEHOLDER_TIME.hours,
      minute,
      daysOfWeek: [],
      dayOfMonth: 1,
    };
  }

  const hours = parseHourList(hourField);
  if (hours === null) return null;

  if (dowField !== "*") {
    // cron ORs day-of-month with day-of-week when both are set — a semantic the
    // editor has no control for, so those expressions stay opaque.
    if (domField !== "*" || monField !== "*") return null;
    if (dowField === "1-5") {
      return {
        repeat: "weekdays",
        interval: 1,
        hours,
        minute,
        daysOfWeek: [1, 2, 3, 4, 5],
        dayOfMonth: 1,
      };
    }
    const days = dowField.split(",").map((d) => parseNumber(d, 0, 6));
    if (days.some((d) => d === null)) return null;
    return {
      repeat: "weekly",
      interval: 1,
      hours,
      minute,
      daysOfWeek: (days as number[]).sort((a, b) => a - b),
      dayOfMonth: 1,
    };
  }

  if (domField === "*" || domField.startsWith("*/")) {
    const interval = parseStep(domField, 31);
    if (interval === null || monField !== "*") return null;
    return { repeat: "daily", interval, hours, minute, daysOfWeek: [], dayOfMonth: 1 };
  }

  const dayOfMonth = parseNumber(domField, 1, 31);
  const monthInterval = parseStep(monField, 12);
  if (dayOfMonth === null || monthInterval === null) return null;
  return {
    repeat: "monthly",
    interval: monthInterval,
    hours,
    minute,
    daysOfWeek: [],
    dayOfMonth,
  };
};

const formatDayList = (days: number[]) =>
  [...days]
    .sort((a, b) => a - b)
    .map((d) => DAY_LABELS[d] ?? String(d))
    .join(", ");

/**
 * The repeat pattern in words, without the time of day ("Every 2 days", "Every
 * week on Mon, Wed").
 *
 * The counterpart to `formatCronShort`, which folds the time in. This is what a
 * row states when the time of day is a control of its own beside it, so
 * including it here would say it twice.
 */
export const describeSchedule = (schedule: CronSchedule) => {
  const n = schedule.interval;
  switch (schedule.repeat) {
    case "minutely":
      return n === 1 ? "Every minute" : `Every ${n} minutes`;
    case "hourly":
      return n === 1 ? "Every hour" : `Every ${n} hours`;
    case "daily":
      return n === 1 ? "Every day" : `Every ${n} days`;
    case "weekdays":
      return "Every weekday";
    case "weekly":
      return schedule.daysOfWeek.length > 0
        ? `Every week on ${formatDayList(schedule.daysOfWeek)}`
        : "Every week";
    case "monthly":
      return n === 1
        ? `Every month on the ${ordinal(schedule.dayOfMonth)}`
        : `Every ${n} months on the ${ordinal(schedule.dayOfMonth)}`;
  }
};

/**
 * Concise label for a 5-field cron schedule — the short form for list rows and
 * schedule summaries ("Daily at 9 AM", "Weekdays at 9 AM", "Every 15 minutes",
 * "Mon/Wed at 8 AM"). Expressions outside the structured model return "Custom".
 */
export const formatCronShort = (cron: string): string => {
  const schedule = parseCron(cron);
  if (!schedule) return "Custom";
  const { interval: n, hours, minute } = schedule;
  const at = formatTimes12Short(hours, minute);
  switch (schedule.repeat) {
    case "minutely":
      return n === 1 ? "Every minute" : `Every ${n} minutes`;
    case "hourly":
      return n === 1 ? "Hourly" : `Every ${n} hours`;
    case "daily":
      return n === 1 ? `Daily at ${at}` : `Every ${n} days at ${at}`;
    case "weekdays":
      return `Weekdays at ${at}`;
    case "weekly":
      return `${schedule.daysOfWeek.map((d) => DAY_LABELS[d]).join("/")} at ${at}`;
    case "monthly":
      return n === 1
        ? `Monthly on the ${ordinal(schedule.dayOfMonth)} at ${at}`
        : `Every ${n} months on the ${ordinal(schedule.dayOfMonth)} at ${at}`;
  }
};
